import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import KeniOrb from '@/components/wrapped/KeniOrb';
import ChatBubble from '@/north-star/ChatBubble';
import { speak } from '@/north-star/speak';
import { TRIAGE, TRIAGE_QUESTIONS } from '@/north-star/NorthStar.content';
import { SCRIPTED_ANSWERS } from '@/north-star/NorthStar.fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

type ChatMsg = { from: 'keni' | 'user'; text: string };

// The Keni triage. Voice-first and hands-free: Keni speaks, then listens, then
// the scripted reply lands and it moves on — while a text box sits at the bottom
// the whole time, so you can type a reply whenever you like. The orb straddles
// the top edge of that bottom bar. When autoplay is off (inspect mode) it holds
// on each step and you advance with Next / Continue.
export type TriageScreenProps = {
  onComplete: () => void;
  /** Leave the conversation — back to the landing screen. */
  onExit: () => void;
  /** Hands-free auto-advance. Off = step manually, to inspect each stage. */
  autoPlay?: boolean;
};

const DONE = TRIAGE_QUESTIONS.length;

const TriageScreen = ({
  onComplete,
  onExit,
  autoPlay = true,
}: TriageScreenProps) => {
  const [index, setIndex] = useState(0);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState('');
  const [transcript, setTranscript] = useState<ChatMsg[]>([
    { from: 'keni', text: TRIAGE_QUESTIONS[0].keni },
  ]);

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcript]);

  const answer = (label: string) => {
    if (index >= DONE) return;
    const next = index + 1;
    setTranscript((prev) => {
      const msgs: ChatMsg[] = [...prev, { from: 'user', text: label }];
      msgs.push({
        from: 'keni',
        text: next < DONE ? TRIAGE_QUESTIONS[next].keni : TRIAGE.thinking,
      });
      return msgs;
    });
    setIndex(next);
  };

  const stepNext = () =>
    answer(SCRIPTED_ANSWERS[index] ?? TRIAGE_QUESTIONS[index].replies[0].label);

  const submitText = () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    answer(value);
  };

  // Hands-free: Keni speaks for a beat, switches to listening, then the scripted
  // reply lands and we advance. With autoplay off it only reaches "listening"
  // and waits — you step forward yourself.
  useEffect(() => {
    if (index >= DONE) return;
    setListening(false);
    const toListening = setTimeout(() => setListening(true), 1000);
    if (!autoPlay) return () => clearTimeout(toListening);
    const toReply = setTimeout(() => {
      answer(
        SCRIPTED_ANSWERS[index] ?? TRIAGE_QUESTIONS[index].replies[0].label,
      );
    }, 2600);
    return () => {
      clearTimeout(toListening);
      clearTimeout(toReply);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, autoPlay]);

  // Once the last answer is in, hold on the "thinking" line, then reveal —
  // unless autoplay is off, when you continue manually.
  useEffect(() => {
    if (index !== DONE || !autoPlay) return;
    const t = setTimeout(onComplete, 1600);
    return () => clearTimeout(t);
  }, [index, autoPlay, onComplete]);

  const q = index < DONE ? TRIAGE_QUESTIONS[index] : undefined;
  const orbState =
    index >= DONE ? 'speaking' : listening ? 'listening' : 'speaking';

  return (
    <ScreenRoot>
      <StatusBar />

      {/* header: just leave. The orb lives at the bottom, centre stage. */}
      <Stack sx={{ alignItems: 'center', px: 2, py: 1.5 }} direction='row'>
        <IconButton aria-label='Leave chat' edge='start' onClick={onExit}>
          <Icon icon={'xmark' as never} fontSize={16} />
        </IconButton>
      </Stack>
      <Divider />

      {/* transcript — extra bottom room so the last bubble clears the orb that
          pokes up out of the input bar below */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: '72px' }}>
        <Stack spacing={1.5}>
          {transcript.map((m, i) => (
            <ChatBubble
              key={i}
              from={m.from}
              text={m.text}
              onReplay={m.from === 'keni' ? () => speak(m.text) : undefined}
            />
          ))}
          <Box ref={bottomRef} />
        </Stack>
      </Box>

      {/* bottom bar — the orb straddles the top edge, a text box always sits
          beneath it */}
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pb: 2,
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
          pt: '52px',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <KeniOrb size={80} state={orbState} />
        </Box>

        <Stack spacing={1.5}>
          <Typography
            sx={{ textAlign: 'center' }}
            variant='caption'
            color='custom.coral.700'
          >
            {listening ? TRIAGE.listening : TRIAGE.speaking}
          </Typography>

          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <TextField
              fullWidth
              size='small'
              variant='outlined'
              placeholder={q?.placeholder ?? 'Type a message…'}
              value={text}
              disabled={!q}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitText();
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: `${radius.xxl}px` },
                '& .MuiOutlinedInput-input': { pl: 2 },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'custom.neutral.200',
                },
              }}
            />
            <IconButton
              aria-label='Send'
              onClick={submitText}
              disabled={!q}
              sx={{
                flexShrink: 0,
                bgcolor: 'custom.coral.600',
                color: 'common.white',
                '&:hover': { bgcolor: 'custom.coral.700' },
                '&.Mui-disabled': {
                  bgcolor: 'custom.neutral.200',
                  color: 'common.white',
                },
              }}
            >
              <Icon icon={'arrow-up' as never} fontSize={16} />
            </IconButton>
          </Stack>

          {/* manual stepping, only while paused for inspection */}
          {!autoPlay && (
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component='button'
                type='button'
                variant='caption'
                onClick={q ? stepNext : onComplete}
              >
                {q ? TRIAGE.next : TRIAGE.continue}
              </Link>
            </Box>
          )}
        </Stack>
      </Box>
    </ScreenRoot>
  );
};

TriageScreen.displayName = 'TriageScreen';
export default TriageScreen;
